import React, { useEffect, useState } from "react";
import { Box, HStack, Spinner, Text, VStack, Badge, Button, Collapse, Divider } from "@chakra-ui/react";
import { getConnectionAccounts, getAccountBalances, getAccountPositions, getAccountActivities } from "../../../../api/SnapTrade";

export const AccountsList: React.FC<{ authorizationId: string }> = ({ authorizationId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [accounts, setAccounts] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [balancesByAccount, setBalancesByAccount] = useState<Record<string, any[]>>({});
  const [positionsByAccount, setPositionsByAccount] = useState<Record<string, any[]>>({});
  const [activitiesByAccount, setActivitiesByAccount] = useState<Record<string, any[]>>({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getConnectionAccounts(authorizationId);
        if (res?.success && Array.isArray(res.data)) {
          setAccounts(res.data);
        }
      } catch (e: any) {
        setError(e?.message || "Failed to load accounts");
      } finally {
        setLoading(false);
      }
    })();
  }, [authorizationId]);

  if (loading) {
    return (
      <HStack>
        <Spinner size="sm" />
        <Text>Loading accounts…</Text>
      </HStack>
    );
  }

  if (error) {
    return <Text color="var(--red)">{error}</Text>;
  }

  if (accounts.length === 0) {
    return <Text fontSize="sm" color="var(--light-gray)">No accounts found for this connection.</Text>;
  }

  return (
    <VStack alignItems="flex-start" spacing={2}>
      {accounts.map((acc: any) => {
        const id = acc?.id || acc?.account_id;
        const isOpen = !!expanded[id];
        return (
          <Box key={id} w="full" border="1px solid var(--gray)" borderRadius="8px" p="10px">
            <HStack spacing={4} justifyContent="space-between">
              <HStack spacing={3}>
                <Text fontWeight={600}>{displayValue(acc?.name || acc?.account_name || acc?.number) || "Account"}</Text>
                {acc?.currency && <Badge>{displayValue(acc.currency)}</Badge>}
                {acc?.market_value !== undefined && (
                  <Badge colorScheme="green">Value: {formatCurrency(acc.market_value, toCurrencyCode(acc?.currency))}</Badge>
                )}
                {acc?.cash !== undefined && (
                  <Badge colorScheme="blue">Cash: {formatCurrency(acc.cash, toCurrencyCode(acc?.currency))}</Badge>
                )}
              </HStack>
              <Button size="sm" onClick={async () => {
                try {
                  setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
                  if (!expanded[id]) {
                    const [bals, poss] = await Promise.all([
                      getAccountBalances(id),
                      getAccountPositions(id),
                    ]);
                    if (bals?.success) setBalancesByAccount((prev) => ({ ...prev, [id]: bals.data }));
                    if (poss?.success) setPositionsByAccount((prev) => ({ ...prev, [id]: poss.data }));
                    // Load latest activities (limit 20)
                    try {
                      const acts = await getAccountActivities(id, { limit: 20 });
                      if (acts?.success) {
                        const normalized = normalizeActivitiesData((acts as any).data);
                        setActivitiesByAccount((prev) => ({ ...prev, [id]: normalized }));
                      }
                    } catch {}
                  }
                } catch {}
              }}>{isOpen ? "Hide" : "View"}</Button>
            </HStack>
            <Collapse in={isOpen} animateOpacity>
              <Divider my={3} />
              <VStack alignItems="flex-start" spacing={2}>
                <Text fontWeight={700}>Balances</Text>
                <HStack wrap="wrap" spacing={2}>
                  {(balancesByAccount[id] || []).map((b: any, idx: number) => (
                    <Badge key={idx}>{displayValue(b?.currency) || ""}: {formatCurrency(b?.cash || b?.amount || 0, toCurrencyCode(b?.currency))}</Badge>
                  ))}
                </HStack>
                <Text fontWeight={700} pt={2}>Positions (top 5)</Text>
                <VStack alignItems="flex-start" spacing={1}>
                  {(positionsByAccount[id] || []).slice(0, 5).map((p: any, idx: number) => (
                    <Text key={idx} fontSize="sm">{displayValue(p?.symbol || p?.ticker || p?.description) || "Instrument"} — Qty: {Number(p?.quantity || p?.units || 0)}</Text>
                  ))}
                </VStack>
                <Text fontWeight={700} pt={2}>Recent activities (latest 20)</Text>
                <VStack alignItems="flex-start" spacing={2} w="full">
                  {normalizeActivitiesData(activitiesByAccount[id]).map((a: any, idx: number) => (
                    <HStack key={a?.id || idx} spacing={3} w="full" justifyContent="space-between">
                      <HStack spacing={3}>
                        <Badge>{displayValue(a?.type || a?.activity_type)}</Badge>
                        <Text fontSize="sm" color="var(--light-gray)">
                          {new Date(a?.trade_date || a?.date || a?.transaction_date || a?.created_at || Date.now()).toLocaleString()}
                        </Text>
                        {extractSymbol(a) && (
                          <Badge colorScheme="purple">{extractSymbol(a)}</Badge>
                        )}
                      </HStack>
                      <HStack spacing={3}>
                        {a?.units !== undefined && (
                          <Badge>{Number(a.units)} units</Badge>
                        )}
                        {a?.price !== undefined && (
                          <Badge colorScheme="gray">@ {formatCurrency(a.price, toCurrencyCode(a?.currency))}</Badge>
                        )}
                        {a?.amount !== undefined && (
                          <Badge colorScheme={Number(a.amount) >= 0 ? "green" : "red"}>
                            {formatCurrency(a.amount, toCurrencyCode(a?.currency))}
                          </Badge>
                        )}
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </Collapse>
          </Box>
        );
      })}
    </VStack>
  );
};

function formatCurrency(value: number, currency?: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(value || 0);
  } catch {
    return String(value);
  }
}

function toCurrencyCode(curr: any): string | undefined {
  if (!curr) return undefined;
  if (typeof curr === 'string') return curr;
  if (typeof curr === 'object') {
    if (typeof curr.code === 'string') return curr.code;
    if (typeof curr.name === 'string') return curr.name;
  }
  return undefined;
}

function displayValue(val: any): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    // Try common fields first
    const candidates = [val.code, val.symbol, val.ticker, val.display_name, val.name, val.id];
    for (const c of candidates) {
      if (typeof c === 'string' || typeof c === 'number' || typeof c === 'boolean') return String(c);
    }
    try {
      return JSON.stringify(val);
    } catch {
      return '[object]';
    }
  }
  return String(val);
}

function normalizeToArray<T = any>(val: any): T[] {
  if (!val) return [];
  if (Array.isArray(val)) return val as T[];
  // some APIs return object maps; convert values to array
  if (typeof val === 'object') return Object.values(val) as T[];
  return [] as T[];
}

function normalizeActivitiesData(val: any): any[] {
  // The example shows { data: [...], pagination: {...} }
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (val && Array.isArray(val.data)) return val.data;
  return normalizeToArray(val);
}

function extractSymbol(a: any): string | undefined {
  if (!a) return undefined;
  // symbol can be object with .symbol, or string, or null
  if (typeof a.symbol === 'string') return a.symbol;
  if (a.symbol && typeof a.symbol === 'object') {
    if (typeof a.symbol.symbol === 'string') return a.symbol.symbol;
    if (typeof a.symbol.raw_symbol === 'string') return a.symbol.raw_symbol;
    if (typeof a.symbol.description === 'string') return a.symbol.description;
  }
  if (a.option_symbol && typeof a.option_symbol === 'object' && typeof a.option_symbol.ticker === 'string') {
    return a.option_symbol.ticker;
  }
  return undefined;
}

export default AccountsList;


